import {defineStore, storeToRefs} from "pinia";
import {computed, Ref, ref, toValue} from "vue";
import {useScopeQuery} from "@/core/store/scopeQuery";
import {Page} from "@/gql/gen";
import merge from "lodash-es/merge";
import trim from "lodash-es/trim";
import trimEnd from "lodash-es/trimEnd";
//@ts-ignore
import {MetaOptions} from "quasar/dist/types/meta";
import {arrayIndex} from "@/core/util/arrayIndex";
import {useConfig} from "@/core/store/config";
import {usePageMeta} from "@/core/store/page-meta";

const STORE_NAME = 'pages'

export const usePagesStore = defineStore(STORE_NAME, () => {

    const pages = ref<Page[]>([])

    const pagesByUrl = computed(() => arrayIndex(pages.value, (page) => trimEnd(page.URL || '', '/')))

    const getPageByUrl = (url: string): Page | undefined => {
        return pages.value.find(page => page.URL && (trim(page.URL, '/') === trim(url, '/')))
    }

    const {registerScopeQuery} = useScopeQuery()

    registerScopeQuery(STORE_NAME, ['app'], {
        page_list: {
            __fragment: 'PageFields'
        },
    }, (data) => {
        pages.value = data.page_list
    })

    return {
        pages,
        pagesByUrl,
        getPageByUrl
    }
})

export function usePage(code: string | Ref<string>, defaultMeta: MetaOptions = {}) {
    const pagesStore = usePagesStore()
    const {pagesByUrl} = storeToRefs(pagesStore)

    const {fillMeta} = usePageMeta()
    const {getConfig} = useConfig()

    const page = computed<Page | null | undefined>(() => {
        const codeValue = trimEnd(toValue(code), '/')
        return pagesByUrl.value[codeValue] || pagesByUrl.value['/' + codeValue]
    })

    const pageMeta = computed<MetaOptions>(() => {

        const result: MetaOptions = {
            meta: {}
        }

        if (page.value) {
            result.title = page.value.META?.TITLE || page.value.NAME || ''
            if (page.value.META?.DESCRIPTION)
                result.meta!.description = {
                    name: 'description',
                    content: page.value.META.DESCRIPTION
                }
            if (page.value.META?.KEYWORDS)
                result.meta!.keywords = {
                    name: 'keywords',
                    content: page.value.META.KEYWORDS
                }
        }

        const meta = fillMeta(merge(defaultMeta, result))

        if (page.value?.CODE === '/')
            meta.titleTemplate = ''

        return meta
    })

    const contentChunksValue = computed(() => {
        return (page.value?.CONTENT_CHUNKS || []).reduce<Record<string, any>>((map, item) => {
            if (item.CODE)
                map[item.CODE] = item.VALUE || ''
            return map
        }, {})
    })

    return {
        page,
        pageMeta,
        contentChunksValue
    }
}

